import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, ArrowRight, FileText, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS_COLORS = {
  received: 'bg-slate-50 text-slate-600 border-slate-300',
  extracting: 'bg-blue-50 text-blue-700 border-blue-300',
  extracted: 'bg-blue-50 text-blue-700 border-blue-300',
  review_required: 'bg-amber-50 text-amber-700 border-amber-300',
  confirmed: 'bg-green-50 text-green-700 border-green-300',
  matching: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  matched: 'bg-green-50 text-green-700 border-green-300',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-300',
  ops_approved: 'bg-teal-50 text-teal-700 border-teal-300',
  settlements_approved: 'bg-teal-50 text-teal-700 border-teal-300',
  finance_approved: 'bg-green-50 text-green-700 border-green-300',
  actualized: 'bg-green-50 text-green-700 border-green-300',
  posted_to_d365: 'bg-green-50 text-green-700 border-green-300',
  closed: 'bg-slate-50 text-slate-600 border-slate-300',
  rejected_no_trade_id: 'bg-red-50 text-red-700 border-red-300',
  rejected_invalid_trade_id: 'bg-red-50 text-red-700 border-red-300',
  rejected_invalid_terms: 'bg-red-50 text-red-700 border-red-300',
  disputed: 'bg-orange-50 text-orange-700 border-orange-300',
  pending_vendor_resubmission: 'bg-amber-50 text-amber-700 border-amber-300',
};

export default function InvoiceListPage() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
        const { data } = await api.get('/invoices', { params });
        setInvoices(data.invoices);
        setTotal(data.total);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">{total} total invoices</p>
        </div>
        <Button onClick={() => navigate('/upload')} data-testid="go-to-upload" className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white">
          <FileText className="h-4 w-4 mr-2" /> New Upload
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoices..."
            data-testid="invoice-search"
            className="pl-10 rounded-sm border-slate-200"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-sm border-slate-200" data-testid="status-filter">
            <Filter className="h-3 w-3 mr-2 text-slate-400" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="extracted">Extracted</SelectItem>
            <SelectItem value="review_required">Review Required</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="finance_approved">Finance Approved</SelectItem>
            <SelectItem value="actualized">Actualized</SelectItem>
            <SelectItem value="rejected_no_trade_id">Rejected (No Trade ID)</SelectItem>
            <SelectItem value="disputed">Disputed</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    {['Invoice #', 'Vendor', 'Trade ID', 'Date', 'Amount', 'Status', 'Confidence', ''].map(h => (
                      <th key={h} className={`text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3 ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}
                        className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        data-testid={`invoice-row-${inv.invoice_number}`}>
                      <td className="px-5 py-3 font-mono text-xs text-slate-900">{inv.invoice_number}</td>
                      <td className="px-5 py-3 text-slate-600">{inv.vendor_name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">{inv.trade_id || '—'}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{inv.invoice_date || '—'}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-900">
                        {inv.currency} {parseFloat(inv.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-wider ${STATUS_COLORS[inv.status] || ''}`}>
                          {inv.status?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${
                          ['very_high','high'].includes(inv.confidence) ? 'border-green-300 text-green-700 bg-green-50' :
                          inv.confidence === 'medium' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                          inv.confidence ? 'border-red-300 text-red-700 bg-red-50' : 'border-slate-200 text-slate-400'
                        }`}>
                          {inv.confidence || 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3"><ArrowRight className="h-3 w-3 text-slate-300" /></td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-12 text-slate-400">No invoices found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
